import React from 'react';

/**
 * TabBar — main horizontal tabs with sliding gold underline indicator.
 * Items: [{ id, label, badge?, hidden? }]
 * Controlled component: parent holds `value`, calls `onChange(id)`.
 *
 * Usage:
 *   <TabBar value={tab} onChange={setTab} items={[
 *     { id: 'overview', label: 'Overview' },
 *     { id: 'trends',   label: 'Trends', badge: 'live' },
 *   ]} />
 */
export function TabBar({ items = [], value, onChange, className = '' }) {
  const wrapRef = React.useRef(null);
  const [ind, setInd] = React.useState({ left: 0, width: 0 });

  const visible = items.filter((i) => !i.hidden);

  const moveTo = React.useCallback((id) => {
    const el = wrapRef.current?.querySelector(`[data-tab="${id}"]`);
    if (!el || !wrapRef.current) return;
    const r = el.getBoundingClientRect();
    const br = wrapRef.current.getBoundingClientRect();
    setInd({ left: r.left - br.left, width: r.width });
  }, []);

  React.useEffect(() => {
    const id = requestAnimationFrame(() => moveTo(value));
    return () => cancelAnimationFrame(id);
  }, [value, items.length, moveTo]);

  React.useEffect(() => {
    const onResize = () => moveTo(value);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [value, moveTo]);

  return (
    <div className={`tabbar relative ${className}`} ref={wrapRef}>
      <nav className="flex items-center gap-7 text-sm overflow-x-auto" role="tablist">
        {visible.map((item) => {
          const active = item.id === value;
          return (
            <button
              key={item.id}
              data-tab={item.id}
              role="tab"
              aria-selected={active}
              onMouseEnter={() => moveTo(item.id)}
              onMouseLeave={() => moveTo(value)}
              onClick={() => onChange?.(item.id)}
              className={
                'py-3 whitespace-nowrap transition-colors flex items-center gap-2 ' +
                (active
                  ? 'text-ink-900 dark:text-ink-50 font-medium'
                  : 'text-ink-500 hover:text-ink-900 dark:hover:text-ink-50')
              }
            >
              <span>{item.label}</span>
              {item.badge ? (
                <span className="pill-2050 pill-neutral text-[10px]">
                  {item.badge}
                </span>
              ) : null}
            </button>
          );
        })}
      </nav>
      <div className="absolute inset-x-0 bottom-0 h-px bg-ink-200/70 dark:bg-ink-800/70" />
      <div className="tab-ind" style={{ left: ind.left, width: ind.width }} />
    </div>
  );
}
export default TabBar;
